/**
 * "Works with Fias AI" — host-chrome discoverability affordance for
 * AI-enabled plugins (design: fias-ai-discoverability Part 1).
 *
 * Rendered by the HOST (PluginRenderer's overlay layer), never by the
 * plugin: the iframe cannot draw outside itself and has no way to open
 * the assistant window, so plugins that declare a Fias AI surface were
 * invisible to any user who hadn't found the header control. This chip
 * is the uniform, zero-plugin-code fix — it appears for every plugin
 * whose merged action list is non-empty (manifest lane for all plugins,
 * runtime lane for trusted ones) and simply opens the global assistant
 * panel on click.
 *
 * Deliberately NOT plugin-triggerable: the user's click on host chrome
 * is the gesture. The plugin-initiated variant (`ai_open`) is Part 2 of
 * the design and carries its own debounce rules.
 *
 * Dismissal is per-arche and per-session (sessionStorage) — a user who
 * closes it isn't nagged again this visit, but discoverability returns
 * next session. Hidden while the panel is open or minimized (the
 * assistant's own floating button covers the minimized state).
 */
import { X, Sparkles } from 'lucide-react';
import { useRef, useState } from 'react';
import { useGlobalFiasAIOptional } from '@/components/fias-ai/hooks/GlobalFiasAIContext';

const DISMISS_KEY_PREFIX = 'fias-ai-chip-dismissed:';

function isDismissed(archeId: string): boolean {
  try {
    return sessionStorage.getItem(`${DISMISS_KEY_PREFIX}${archeId}`) === '1';
  } catch {
    return false;
  }
}

export function FiasAIDiscoveryChip({ archeId }: { archeId: string }) {
  const fiasAI = useGlobalFiasAIOptional();
  // Dismissal is read from storage on EVERY render, keyed by the CURRENT
  // archeId — the renderer (and this chip) survive arche-to-arche
  // navigation, so state captured at mount would leak one arche's
  // dismissal onto the next (and ignore the next one's stored dismissal).
  // The counter only forces the re-render after a dismiss click; the ref is
  // the in-memory fallback for storage-less environments (private mode).
  const [, bumpDismissal] = useState(0);
  const locallyDismissedRef = useRef<Set<string>>(new Set());

  // No assistant on this surface (no provider) → nothing to point at.
  if (
    !fiasAI ||
    isDismissed(archeId) ||
    locallyDismissedRef.current.has(archeId) ||
    fiasAI.isPanelOpen ||
    fiasAI.isMinimized
  ) {
    return null;
  }

  return (
    <div
      className="absolute bottom-3 left-3 z-20 flex items-center gap-1 rounded-full border bg-background/95 shadow-sm backdrop-blur-sm"
      data-testid="fias-ai-discovery-chip"
    >
      <button
        type="button"
        onClick={() => fiasAI.openPanel()}
        className="flex items-center gap-1.5 rounded-full py-1.5 pl-3 pr-1 text-xs font-medium text-foreground hover:text-primary"
        title="This app works with Fias AI — open the assistant"
      >
        <Sparkles className="h-3.5 w-3.5 text-primary" />
        Works with Fias AI
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => {
          try {
            sessionStorage.setItem(`${DISMISS_KEY_PREFIX}${archeId}`, '1');
          } catch {
            // Storage unavailable (private mode) — the local set below still
            // hides the chip for this mount; it may return on remount.
          }
          locallyDismissedRef.current.add(archeId);
          bumpDismissal((n) => n + 1);
        }}
        className="rounded-full p-1.5 text-muted-foreground hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
